import { useState } from "react";
import { Clock, Lock, Send, ShieldOff, UserMinus } from "lucide-react";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/app/_module/components/ui/alert-dialog";
import { Button } from "@/app/_module/components/ui/button";
import type { DisableEligibility } from "../../types/types";

export type RemoveAgentMode = "remove" | "suspend" | "disable" | "request";

interface RemoveAgentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  agentName: string;
  /** From the backend: act outright, only request it, or neither. Undefined while loading. */
  eligibility?: DisableEligibility;
  /** `scheduleDeletion` applies to "disable"; `autoDisableAfterDays` to "suspend". */
  onChoose: (
    mode: RemoveAgentMode,
    opts: { scheduleDeletion: boolean; autoDisableAfterDays: number | null },
  ) => void;
}

/**
 * Three different things an owner might mean by "get rid of this agent", kept
 * apart on purpose because they are not equally reversible:
 *
 *   Remove   — off the roster, account untouched. They carry on independently.
 *   Suspend  — cannot sign in. Billing, numbers, book and roster all stay. Undo anytime.
 *   Disable  — cannot sign in AND the subscription is cancelled and numbers released,
 *              with no refund. Optionally starts a 30-day deletion clock.
 *
 * The deletion clock is a TOGGLE, never automatic: disabling is an access decision,
 * deleting someone's book is a data decision, and an owner should make them separately.
 */
export function RemoveAgentDialog({ open, onOpenChange, agentName, eligibility, onChoose }: RemoveAgentDialogProps) {
  const [scheduleDeletion, setScheduleDeletion] = useState(false);
  const [autoDisable, setAutoDisable] = useState(false);
  // The parent closes the dialog in onChoose. Closing it here went through onOpenChange,
  // which parents treat as Cancel and clear the picked agent, so after 2FA there was no
  // one to act on: Remove/Suspend/Disable did nothing from 09-08 to 10-08.
  const choose = (mode: RemoveAgentMode) => {
    onChoose(mode, {
      scheduleDeletion: mode === "disable" ? scheduleDeletion : false,
      autoDisableAfterDays: mode === "suspend" && autoDisable ? 30 : null,
    });
    setScheduleDeletion(false);
    setAutoDisable(false);
  };
  const pending = !!eligibility?.pendingRequestId;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-h-[85vh] overflow-y-auto">
        <AlertDialogHeader>
          <AlertDialogTitle>Manage {agentName}</AlertDialogTitle>
          <AlertDialogDescription>Choose what happens to their account.</AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-2">
          <button
            type="button"
            onClick={() => choose("remove")}
            className="w-full rounded-md border p-3 text-left transition-colors hover:bg-muted cursor-pointer"
          >
            <div className="flex items-center gap-2 font-medium">
              <UserMinus className="h-4 w-4" />
              Remove from agency
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              They leave your agency and continue as an independent agent. Their account, subscription and numbers are untouched.
            </p>
          </button>

          {eligibility?.canDisable && (
            <div className="rounded-md border border-amber-200 bg-amber-50/60 p-3">
              <button type="button" onClick={() => choose("suspend")} className="w-full text-left cursor-pointer">
                <div className="flex items-center gap-2 font-medium text-amber-800">
                  <Lock className="h-4 w-4" />
                  Suspend — block sign-in, keep everything
                </div>
                <p className="mt-1 text-sm text-amber-900/80">
                  They are signed out and cannot log back in. Their subscription, phone numbers, leads and place on your
                  roster all stay exactly as they are — so a long investigation never costs them their number. Lift it
                  whenever you like.
                </p>
              </button>
              <label
                className="mt-2.5 flex cursor-pointer items-start gap-2 rounded border border-amber-200 bg-white/70 p-2 text-sm"
                onClick={(e) => e.stopPropagation()}
              >
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 accent-amber-600"
                  checked={autoDisable}
                  onChange={(e) => setAutoDisable(e.target.checked)}
                />
                <span>
                  <span className="font-medium text-amber-900">Auto-disable after 30 days</span>
                  <span className="mt-0.5 block text-amber-900/70">
                    Off by default. Leave it off and the suspension simply stays until you act — we will never cancel a
                    subscription or release a phone number on a timer. Tick it and, after 30 days, their billing stops
                    and their numbers are released automatically. Their leads and policies are kept either way.
                  </span>
                </span>
              </label>
            </div>
          )}

          {eligibility?.canDisable && (
            <div className="rounded-md border border-red-200 bg-red-50/60 p-3">
              <button type="button" onClick={() => choose("disable")} className="w-full text-left cursor-pointer">
                <div className="flex items-center gap-2 font-medium text-red-700">
                  <ShieldOff className="h-4 w-4" />
                  Disable — block sign-in and end billing
                </div>
                <p className="mt-1 text-sm text-red-900/80">
                  Everything Suspend does, plus their subscription is cancelled and every phone number they hold is
                  released. There is no refund, and a released number cannot be reclaimed.
                </p>
              </button>
              <label
                className="mt-2.5 flex cursor-pointer items-start gap-2 rounded border border-red-200 bg-white/70 p-2 text-sm"
                onClick={(e) => e.stopPropagation()}
              >
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 accent-red-600"
                  checked={scheduleDeletion}
                  onChange={(e) => setScheduleDeletion(e.target.checked)}
                />
                <span>
                  <span className="font-medium text-red-800">Also delete their data after 30 days</span>
                  <span className="mt-0.5 block text-red-900/70">
                    Off by default. With this on, the account and its leads and policies are permanently removed 30 days
                    from now. You can restore them at any point before that.
                  </span>
                </span>
              </label>
            </div>
          )}

          {eligibility?.canRequest && !pending && (
            <button
              type="button"
              onClick={() => choose("request")}
              className="w-full rounded-md border border-amber-200 bg-amber-50/60 p-3 text-left transition-colors hover:bg-amber-50 cursor-pointer"
            >
              <div className="flex items-center gap-2 font-medium text-amber-800">
                <Send className="h-4 w-4" />
                Request account deactivation
              </div>
              <p className="mt-1 text-sm text-amber-900/80">
                Their primary agency is elsewhere, so only InsuraCentral can lock this account. We review the request and
                apply it the same way if approved.
              </p>
            </button>
          )}

          {eligibility?.canRequest && pending && (
            <div className="w-full rounded-md border border-amber-200 bg-amber-50/40 p-3 text-left">
              <div className="flex items-center gap-2 font-medium text-amber-800">
                <Clock className="h-4 w-4" />
                Deactivation request pending
              </div>
              <p className="mt-1 text-sm text-amber-900/80">
                InsuraCentral is reviewing a request for this agent. You can still remove them from your agency now.
              </p>
            </div>
          )}
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel asChild>
            <Button variant="outline">Cancel</Button>
          </AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
